import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import PropertyCard from './components/PropertyCard';
import FilterPanel from './components/FilterPanel';
import MapView from './components/MapView';
import SortDropdown from './components/SortDropdown';
import BiddingPanel from './components/BiddingPanel';
import { apiRequest } from '../../services/apiClient';
import { createMarketBid } from '../../services/bidApi';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const PropertyListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, role, user } = useAuth();
  const {
    warning: toastWarning,
    info: toastInfo,
    success: toastSuccess,
    error: toastError,
  } = useToast();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const [isBiddingOpen, setIsBiddingOpen] = useState(false);
  const lastPropertyElementRef = useRef();
  const isMarketUser = `${role || ''}`.toUpperCase() === 'MARKET_USER';
  const ownProductBidTooltip = 'Bu ürün size ait. Kendi ürününüze teklif gönderemezsiniz.';
  const ownProductContactTooltip = 'Bu ürün size ait. Kendinizle iletişime geçemezsiniz.';
  const isOwnProduct = (product) =>
    Boolean(user?.id && product?.seller?.id && user.id === product.seller.id);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      try {
        const requestParams = new URLSearchParams();
        const query = searchParams?.get('query');
        const location = searchParams?.get('location');
        const productType = searchParams?.get('productType');
        const minPrice = searchParams?.get('minPrice');
        const maxPrice = searchParams?.get('maxPrice');

        if (query) requestParams.set('query', query);
        if (location) requestParams.set('location', location);
        if (productType && productType !== 'all') requestParams.set('productType', productType);
        if (minPrice) requestParams.set('minPrice', minPrice);
        if (maxPrice) requestParams.set('maxPrice', maxPrice);

        const queryString = requestParams.toString();
        const response = await apiRequest(`/products${queryString ? `?${queryString}` : ''}`);
        const items = Array.isArray(response) ? response : response?.items || [];

        setProperties(items);
        setFilteredProperties(sortProperties(items, sortBy));
      } catch (error) {
        console.error('Error fetching products:', error);
        setProperties([]);
        setFilteredProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchParams]);

  // Sort properties
  const sortProperties = (propertiesToSort, sortOption) => {
    const sorted = [...propertiesToSort];
    
    switch (sortOption) {
      case 'price-low':
        return sorted?.sort((a, b) => a?.price - b?.price);
      case 'price-high':
        return sorted?.sort((a, b) => b?.price - a?.price);
      case 'newest':
        return sorted?.sort((a, b) => a?.daysListed - b?.daysListed);
      case 'oldest':
        return sorted?.sort((a, b) => b?.daysListed - a?.daysListed);
      case 'quantity':
        return sorted?.sort(
          (a, b) =>
            Number(b?.availableQuantity ?? b?.quantity ?? 0) -
            Number(a?.availableQuantity ?? a?.quantity ?? 0),
        );
      default:
        return sorted;
    }
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    const sorted = sortProperties(filteredProperties, newSortBy);
    setFilteredProperties(sorted);
  };

  // Handle filter changes
  const handleFilterChange = (filters) => {
    const newSearchParams = new URLSearchParams();
    
    Object.entries(filters)?.forEach(([key, value]) => {
      if (value && value !== '' && value !== 'all') {
        newSearchParams?.set(key, value);
      }
    });
    
    setSearchParams(newSearchParams);
  };

  // Handle property save/unsave
  const handlePropertySave = (propertyId, isSaved) => {
    setProperties(prev => prev?.map(property =>
      property?.id === propertyId ? { ...property, isSaved } : property
    ));
    setFilteredProperties(prev => prev?.map(property =>
      property?.id === propertyId ? { ...property, isSaved } : property
    ));
  };

  const ensureMarketUserAccess = ({
    loginMessage,
    nonMarketUserMessage,
    postLoginAction,
    fromPath,
  } = {}) => {
    if (!isAuthenticated) {
      const loginFrom =
        fromPath
        || (postLoginAction?.type === 'open-contact' && postLoginAction?.productId
          ? `/property-details?id=${postLoginAction.productId}`
          : `${location.pathname}${location.search}`);
      toastInfo(loginMessage || 'Bu işlem için pazaryeri hesabı ile giriş yapmalısınız.');
      navigate('/login', {
        replace: false,
        state: {
          from: loginFrom,
          postLoginAction,
        },
      });
      return false;
    }

    if (!isMarketUser) {
      toastWarning(nonMarketUserMessage || 'Bu işlem sadece pazaryeri kullanıcıları tarafından yapılabilir.');
      return false;
    }

    return true;
  };

  const handleContactSeller = (product) => {
    if (!product?.id) {
      return;
    }

    if (isOwnProduct(product)) {
      toastWarning('Bu ürün size ait. Kendinizle iletişime geçemezsiniz.');
      return;
    }

    if (
      !ensureMarketUserAccess({
        loginMessage: 'Satıcıya ulaşmak için pazaryeri hesabı ile giriş yapmalısınız.',
        nonMarketUserMessage: 'Satıcıya ulaşma sadece pazaryeri kullanıcıları tarafından yapılabilir.',
        postLoginAction: {
          type: 'open-contact',
          productId: product.id,
        },
        fromPath: `/property-details?id=${product.id}`,
      })
    ) {
      return;
    }

    navigate(`/property-details?id=${product.id}`, {
      state: { openContactModal: true },
    });
  };

  // Handle bid submission
  const handleBidSubmit = async (bidData) => {
    const targetProduct = properties.find((item) => item?.id === bidData?.productId)
      || filteredProperties.find((item) => item?.id === bidData?.productId)
      || selectedProperty;

    if (isOwnProduct(targetProduct)) {
      toastWarning('Bu ürün size ait. Kendi ürününüze teklif gönderemezsiniz.');
      return;
    }

    if (!ensureMarketUserAccess()) {
      return;
    }

    try {
      const createdBid = await createMarketBid({
        productId: bidData?.productId,
        amount: bidData?.amount,
        quantity: bidData?.quantity,
        note: bidData?.message || undefined,
      });

      const { productId } = bidData;
      const newBid = {
        id: createdBid?.id || Date.now(),
        buyerId: createdBid?.buyer?.id || user?.id || null,
        amount: Number(createdBid?.amount || bidData?.amount || 0),
        quantity: Number(createdBid?.quantity || bidData?.quantity || 0),
        acceptedQuantity: createdBid?.acceptedQuantity || null,
        bidder: createdBid?.buyer?.fullName || createdBid?.buyer?.email || 'Kullanıcı',
        status: createdBid?.status || 'PENDING',
        timestamp: createdBid?.createdAt || new Date().toISOString(),
      };

      // Update the product with new bid
      const updateProductWithBid = (product) =>
        product?.id === productId
          ? { ...product, currentBids: [newBid, ...(product?.currentBids || [])] }
          : product;

      setProperties((prev) => prev?.map(updateProductWithBid));
      setFilteredProperties((prev) => prev?.map(updateProductWithBid));
      setSelectedProperty((prev) => (prev?.id === productId ? updateProductWithBid(prev) : prev));

      setIsBiddingOpen(false);
      toastSuccess('Teklif başarıyla gönderildi.');
    } catch (error) {
      toastError(error?.message || 'Teklif gönderilemedi.');
      throw error;
    }
  };

  // Get breadcrumb items
  const getBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Home', path: '/homepage' },
      { label: 'Products', path: '/property-listings' }
    ];

    const location = searchParams?.get('location');
    const productType = searchParams?.get('productType');

    if (location) {
      breadcrumbs?.push({ label: location, path: null });
    }

    if (productType && productType !== 'all') {
      breadcrumbs?.push({ 
        label: productType?.charAt(0)?.toUpperCase() + productType?.slice(1), 
        path: null 
      });
    }

    return breadcrumbs;
  };

  // Check if any filters are active
  const hasActiveFilters = searchParams?.get('query') || searchParams?.get('location') || 
                           searchParams?.get('productType') || searchParams?.get('minPrice') || 
                           searchParams?.get('maxPrice');

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 lg:pt-18">
        {/* Breadcrumb */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center space-x-2 text-sm">
              {getBreadcrumbs()?.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <Icon name="ChevronRight" size={14} className="text-text-secondary" />
                  )}
                  {crumb?.path ? (
                    <Link
                      to={crumb?.path}
                      className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                    >
                      {crumb?.label}
                    </Link>
                  ) : (
                    <span className="text-text-primary font-medium">{crumb?.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        {/* Search Results Header */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  Farm Products for Sale
                </h1>
                <p className="text-text-secondary mt-1">
                  {loading ? 'Loading...' : `${filteredProperties?.length} products found`}
                </p>
              </div>

              <div className="flex items-center space-x-3">
                {/* View Toggle (Mobile) */}
                <div className="flex lg:hidden bg-secondary-100 rounded-md p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                      viewMode === 'list' ?'bg-surface text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <Icon name="List" size={16} className="inline mr-1" />
                    List
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                      viewMode === 'map' ?'bg-surface text-text-primary shadow-sm' :'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <Icon name="Map" size={16} className="inline mr-1" />
                    Map
                  </button>
                </div>

                {/* Sort Dropdown */}
                <SortDropdown value={sortBy} onChange={handleSortChange} />

                {/* Filter Toggle */}
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-700 transition-all duration-200 ease-out micro-interaction"
                >
                  <Icon name="SlidersHorizontal" size={16} />
                  <span className="hidden sm:inline">Filters</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto">
          <div className="flex">
            {/* Filter Panel */}
            <FilterPanel
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              onFilterChange={handleFilterChange}
              initialFilters={{
                query: searchParams?.get('query') || '',
                location: searchParams?.get('location') || '',
                productType: searchParams?.get('productType') || '',
                minPrice: searchParams?.get('minPrice') || '',
                maxPrice: searchParams?.get('maxPrice') || ''
              }}
            />

            {/* Content Area */}
            <div className="flex-1 min-w-0">
              {/* Desktop Split View */}
              <div className="hidden lg:flex h-[calc(100vh-200px)]">
                {hasActiveFilters ? (
                  /* Filtered View - 4 Column Grid without Map */
                  (<div className="w-full overflow-y-auto">
                    <div className="p-6">
                      {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                          {[...Array(8)]?.map((_, index) => (
                            <div key={index} className="card p-4">
                              <div className="animate-pulse">
                                <div className="w-full h-48 bg-secondary-200 rounded-md mb-4"></div>
                                <div className="space-y-3">
                                  <div className="h-4 bg-secondary-200 rounded w-3/4"></div>
                                  <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                                  <div className="h-3 bg-secondary-200 rounded w-2/3"></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                          {filteredProperties?.map((property, index) => (
                            <div
                              key={property?.id}
                              ref={index === filteredProperties?.length - 1 ? lastPropertyElementRef : null}
                            >
                              <PropertyCard
                                property={property}
                                variant="card"
                                onSave={handlePropertySave}
                                canBid={isAuthenticated && isMarketUser}
                                isOwnProduct={isOwnProduct(property)}
                                ownProductBidTooltip={ownProductBidTooltip}
                                ownProductContactTooltip={ownProductContactTooltip}
                                onContactClick={handleContactSeller}
                                onBidClick={() => {
                                  if (isOwnProduct(property)) {
                                    return;
                                  }
                                  if (property?.openToOffer === false) {
                                    toastWarning('Bu ürün teklife kapalı.');
                                    return;
                                  }
                                  if (!ensureMarketUserAccess()) {
                                    return;
                                  }
                                  setSelectedProperty(property);
                                  setIsBiddingOpen(true);
                                }}
                              />
                            </div>
                          ))}
                          
                          {filteredProperties?.length === 0 && (
                            <div className="col-span-full text-center py-12">
                              <Icon name="Search" size={48} className="text-secondary mx-auto mb-4" />
                              <h3 className="text-lg font-semibold text-text-primary mb-2">
                                No products found
                              </h3>
                              <p className="text-text-secondary">
                                Try adjusting your search criteria or filters
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>)
                ) : (
                  /* Default View - List with Map */
                  (<>
                    {/* Product List */}
                    <div className="w-2/5 overflow-y-auto">
                      <div className="p-6">
                        {loading ? (
                          <div className="grid grid-cols-1 gap-6">
                            {[...Array(6)]?.map((_, index) => (
                              <div key={index} className="card p-4">
                                <div className="animate-pulse">
                                  <div className="flex space-x-4">
                                    <div className="w-48 h-32 bg-secondary-200 rounded-md"></div>
                                    <div className="flex-1 space-y-3">
                                      <div className="h-4 bg-secondary-200 rounded w-3/4"></div>
                                      <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                                      <div className="h-3 bg-secondary-200 rounded w-2/3"></div>
                                      <div className="flex space-x-2">
                                        <div className="h-3 bg-secondary-200 rounded w-16"></div>
                                        <div className="h-3 bg-secondary-200 rounded w-16"></div>
                                        <div className="h-3 bg-secondary-200 rounded w-16"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {filteredProperties?.map((property, index) => (
                              <div
                                key={property?.id}
                                ref={index === filteredProperties?.length - 1 ? lastPropertyElementRef : null}
                                onMouseEnter={() => setSelectedProperty(property)}
                                onMouseLeave={() => setSelectedProperty(null)}
                              >
                                <PropertyCard
                                  property={property}
                                  variant="list"
                                  onSave={handlePropertySave}
                                  isHighlighted={selectedProperty?.id === property?.id}
                                  canBid={isAuthenticated && isMarketUser}
                                  isOwnProduct={isOwnProduct(property)}
                                  ownProductBidTooltip={ownProductBidTooltip}
                                  ownProductContactTooltip={ownProductContactTooltip}
                                  onContactClick={handleContactSeller}
                                  onBidClick={() => {
                                    if (isOwnProduct(property)) {
                                      return;
                                    }
                                    if (property?.openToOffer === false) {
                                      toastWarning('Bu ürün teklife kapalı.');
                                      return;
                                    }
                                    if (!ensureMarketUserAccess()) {
                                      return;
                                    }
                                    setSelectedProperty(property);
                                    setIsBiddingOpen(true);
                                  }}
                                />
                              </div>
                            ))}
                            
                            {filteredProperties?.length === 0 && (
                              <div className="text-center py-12">
                                <Icon name="Search" size={48} className="text-secondary mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-text-primary mb-2">
                                  No products found
                                </h3>
                                <p className="text-text-secondary">
                                  Try adjusting your search criteria or filters
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Map and Bidding Panel */}
                    <div className="w-3/5 border-l border-border flex">
                      {/* Map View */}
                      <div className="flex-1">
                        <MapView
                          properties={filteredProperties}
                          selectedProperty={selectedProperty}
                          onPropertySelect={setSelectedProperty}
                        />
                      </div>
                      
                      {/* Bidding Panel */}
                      <div className="w-80 border-l border-border">
                        <BiddingPanel
                          selectedProduct={selectedProperty}
                          onBidSubmit={handleBidSubmit}
                          isOpen={selectedProperty !== null}
                          canBid={isAuthenticated && isMarketUser}
                          isOwnProduct={isOwnProduct(selectedProperty)}
                          ownProductBidTooltip={ownProductBidTooltip}
                          ownProductContactTooltip={ownProductContactTooltip}
                          onRequireMarketUser={ensureMarketUserAccess}
                          onContactSeller={handleContactSeller}
                        />
                      </div>
                    </div>
                  </>)
                )}
              </div>

              {/* Mobile View */}
              <div className="lg:hidden">
                {viewMode === 'list' ? (
                  <div className="p-4">
                    {loading ? (
                      <div className="grid grid-cols-1 gap-4">
                        {[...Array(6)]?.map((_, index) => (
                          <div key={index} className="card p-4">
                            <div className="animate-pulse">
                              <div className="w-full h-48 bg-secondary-200 rounded-md mb-4"></div>
                              <div className="space-y-3">
                                <div className="h-4 bg-secondary-200 rounded w-3/4"></div>
                                <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                                <div className="h-3 bg-secondary-200 rounded w-2/3"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filteredProperties?.map((property, index) => (
                          <div
                            key={property?.id}
                            ref={index === filteredProperties?.length - 1 ? lastPropertyElementRef : null}
                          >
                            <PropertyCard
                              property={property}
                              variant="card"
                              onSave={handlePropertySave}
                              canBid={isAuthenticated && isMarketUser}
                              isOwnProduct={isOwnProduct(property)}
                              ownProductBidTooltip={ownProductBidTooltip}
                              ownProductContactTooltip={ownProductContactTooltip}
                              onContactClick={handleContactSeller}
                              onBidClick={() => {
                                if (isOwnProduct(property)) {
                                  return;
                                }
                                if (property?.openToOffer === false) {
                                  toastWarning('Bu ürün teklife kapalı.');
                                  return;
                                }
                                if (!ensureMarketUserAccess()) {
                                  return;
                                }
                                setSelectedProperty(property);
                                setIsBiddingOpen(true);
                              }}
                            />
                          </div>
                        ))}
                        
                        {filteredProperties?.length === 0 && (
                          <div className="text-center py-12">
                            <Icon name="Search" size={48} className="text-secondary mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-text-primary mb-2">
                              No products found
                            </h3>
                            <p className="text-text-secondary">
                              Try adjusting your search criteria or filters
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-[calc(100vh-200px)]">
                    <MapView
                      properties={filteredProperties}
                      selectedProperty={selectedProperty}
                      onPropertySelect={setSelectedProperty}
                      isMobile={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Bidding Modal */}
        {isBiddingOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/50 z-50 flex items-end">
            <div className="bg-surface w-full max-h-[80vh] rounded-t-xl">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="text-lg font-semibold">Teklif Gönder</h3>
                <button
                  onClick={() => setIsBiddingOpen(false)}
                  className="p-2 hover:bg-secondary-100 rounded-md"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="overflow-y-auto">
                <BiddingPanel
                  selectedProduct={selectedProperty}
                  onBidSubmit={handleBidSubmit}
                  isOpen={true}
                  isMobile={true}
                  canBid={isAuthenticated && isMarketUser}
                  isOwnProduct={isOwnProduct(selectedProperty)}
                  ownProductBidTooltip={ownProductBidTooltip}
                  ownProductContactTooltip={ownProductContactTooltip}
                  onRequireMarketUser={ensureMarketUserAccess}
                  onContactSeller={handleContactSeller}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PropertyListings;
